/**
 * Enhanced Curriculum File Browser with Media Viewing
 * Tree-based file browser with integrated media viewers for text, JSON, audio, and video
 */

import React, { useState, useEffect } from 'react';
import { 
  ChevronRight, 
  ChevronDown, 
  Folder, 
  FileText, 
  Calendar, 
  Book,
  Play,
  Music,
  Video,
  Eye
} from 'lucide-react';
import { MediaViewerModal } from '../modals/MediaViewerModal';
import { FloatingAudioPlayer } from '../media/FloatingAudioPlayer';
import { FileType, getFileType, getFileIcon } from '../../utils/fileTypes';

interface CurriculumNode {
  name: string;
  path: string;
  type: 'directory' | 'file';
  children?: CurriculumNode[];
  isExpanded?: boolean;
  content?: any;
}

interface EnhancedCurriculumBrowserProps {
  selectedFile: string | null;
  onFileSelect: (filePath: string, content: any) => void;
}

const EnhancedCurriculumBrowser: React.FC<EnhancedCurriculumBrowserProps> = ({
  selectedFile,
  onFileSelect
}) => {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://api.hi-si.ai';
  const [curriculumTree, setCurriculumTree] = useState<CurriculumNode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Media viewer states
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState<{ name: string; path: string } | null>(null);
  const [audioPlayer, setAudioPlayer] = useState<{ name: string; path: string } | null>(null);
  
  // Lesson titles cache
  const [lessonTitles, setLessonTitles] = useState<Map<string, string>>(new Map());

  // Fetch lesson title from content
  const fetchLessonTitle = async (filePath: string): Promise<string | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/curriculum/lesson?path=${encodeURIComponent(filePath)}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const content = await response.json();
        // Extract title from the first page's peer tone (most common pattern)
        const title = content?.pages?.[0]?.copy?.['en-US']?.peer?.title || 
                     content?.pages?.[0]?.copy?.['en-US']?.drill?.title ||
                     content?.pages?.[0]?.copy?.['en-US']?.mentor?.title ||
                     null;
        return title;
      }
    } catch (err) {
      console.debug('Failed to fetch lesson title:', filePath, err);
    }
    return null;
  };

  // Load curriculum tree from backend API
  useEffect(() => {
    const loadCurriculumStructure = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/curriculum/structure`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch curriculum structure');
        }
        
        const data = await response.json();
        
        // Transform data into tree structure
        const rootNode: CurriculumNode = {
          name: 'Curriculum',
          path: '/content/curriculum/baseline-en-US/drafts/content',
          type: 'directory',
          isExpanded: true,
          children: data.children.map((module: any) => ({
            ...module,
            isExpanded: false
          }))
        };
        
        setCurriculumTree(rootNode);
        
        // Fetch lesson titles for all day*.json files
        const fetchTitlesForNode = async (node: CurriculumNode) => {
          if (node.type === 'file' && node.name.endsWith('.json') && node.name.startsWith('day')) {
            const title = await fetchLessonTitle(node.path);
            if (title) {
              setLessonTitles(prev => new Map(prev).set(node.path, title));
            }
          }
          if (node.children) {
            await Promise.all(node.children.map(fetchTitlesForNode));
          }
        };
        
        // Fetch titles in background (non-blocking)
        fetchTitlesForNode(rootNode).catch(err => 
          console.debug('Failed to fetch some lesson titles:', err)
        );
        
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load curriculum');
      } finally {
        setLoading(false);
      }
    };

    loadCurriculumStructure();
  }, []);

  // Handle lesson selection for execution (primary action)
  const handleLessonSelect = async (filePath: string, _fileName: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/curriculum/lesson?path=${encodeURIComponent(filePath)}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const content = await response.json();
        onFileSelect(filePath, content);
      }
    } catch (err) {
      console.error('Failed to load lesson:', err);
    }
  };

  // Handle media viewing (secondary action via button)
  const handleFileView = (filePath: string, fileName: string) => {
    const fileType = getFileType(fileName);
    
    switch (fileType) {
      case FileType.TEXT:
      case FileType.JSON:
        setViewingFile({ name: fileName, path: filePath });
        setMediaViewerOpen(true);
        break;
      
      case FileType.AUDIO:
        setAudioPlayer({ name: fileName, path: `${API_BASE_URL}${filePath}` });
        break;
      
      case FileType.VIDEO:
        setViewingFile({ name: fileName, path: `${API_BASE_URL}${filePath}` });
        setMediaViewerOpen(true);
        break;
        
      default:
        setViewingFile({ name: fileName, path: filePath });
        setMediaViewerOpen(true);
        break;
    }
  };

  const toggleNodeExpansion = (nodePath: string) => {
    const updateNode = (node: CurriculumNode): CurriculumNode => {
      if (node.path === nodePath) {
        return { ...node, isExpanded: !node.isExpanded };
      }
      if (node.children) {
        return {
          ...node,
          children: node.children.map(updateNode)
        };
      }
      return node;
    };

    setCurriculumTree(prevTree => prevTree ? updateNode(prevTree) : null);
  };

  const getFileDisplayIcon = (fileName: string, fileType: FileType) => {
    // Special handling for curriculum lesson files
    if (fileName.endsWith('.json') && fileName.startsWith('day')) {
      return <Calendar className="w-4 h-4 text-cyan-400" />;
    }

    switch (fileType) {
      case FileType.TEXT:
        return <FileText className="w-4 h-4 text-gray-500" />;
      case FileType.JSON:
        return <Book className="w-4 h-4 text-orange-500" />;
      case FileType.AUDIO:
        return <Music className="w-4 h-4 text-purple-500" />;
      case FileType.VIDEO:
        return <Video className="w-4 h-4 text-red-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-400" />;
    }
  };

  const renderFileAction = (fileName: string, filePath: string, fileType: FileType) => {
    const isLessonFile = fileName.endsWith('.json') && fileName.startsWith('day');
    
    // For lesson files, only show the eye icon for viewing
    if (isLessonFile) {
      return (
        <button
          type="button"
          title="View lesson content"
          className="p-1 rounded hover:bg-cyan-500/20 text-cyan-400"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleFileView(filePath, fileName);
          }}
        >
          <Eye className="w-3 h-3" />
        </button>
      );
    }

    // For other file types, show appropriate action buttons
    switch (fileType) {
      case FileType.TEXT:
      case FileType.JSON:
        return (
          <button
            type="button"
            title="View/edit file"
            className="p-1 rounded hover:bg-cyan-500/20 text-cyan-400"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFileView(filePath, fileName);
            }}
          >
            <Eye className="w-3 h-3" />
          </button>
        );
      case FileType.AUDIO:
        return (
          <button
            type="button"
            title="Play audio"
            className="p-1 rounded hover:bg-purple-100 text-purple-500"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFileView(filePath, fileName);
            }}
          >
            <Play className="w-3 h-3" />
          </button>
        );
      case FileType.VIDEO:
        return (
          <button
            type="button"
            title="Play video"
            className="p-1 rounded hover:bg-red-100 text-red-500"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFileView(filePath, fileName);
            }}
          >
            <Play className="w-3 h-3" />
          </button>
        );
      default:
        return null;
    }
  };

  const renderTreeNode = (node: CurriculumNode, depth = 0): React.ReactNode => {
    const isSelected = selectedFile === node.path;
    const paddingLeft = depth * 20;
    
    if (node.type === 'directory') {
      return (
        <div key={node.path}>
          <div
            className={`flex items-center gap-2 py-1 px-2 cursor-pointer hover:bg-cyan-500/30 ${
              isSelected ? 'bg-cyan-500/10 border-l-2 border-cyan-500' : ''
            }`}
            style={{ paddingLeft: paddingLeft + 8 }}
            onClick={() => toggleNodeExpansion(node.path)}
          >
            {node.isExpanded ? (
              <ChevronDown className="w-4 h-4 text-white/50" />
            ) : (
              <ChevronRight className="w-4 h-4 text-white/50" />
            )}
            <Folder className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-white/70 truncate">{node.name}</span>
          </div>
          
          {node.isExpanded && node.children && (
            <div>
              {node.children.map(child => renderTreeNode(child, depth + 1))}
            </div>
          )}
        </div>
      );
    } else {
      const fileType = getFileType(node.name);
      const isLessonFile = node.name.endsWith('.json') && node.name.startsWith('day');
      
      return (
        <div
          key={node.path}
          className={`flex items-center justify-between gap-2 py-1 px-2 cursor-pointer hover:bg-cyan-500/30 group ${
            isSelected ? 'bg-cyan-500/10 border-l-2 border-cyan-500' : ''
          }`}
          style={{ paddingLeft: paddingLeft + 24 }}
          onClick={() => {
            if (isLessonFile) {
              handleLessonSelect(node.path, node.name);
            }
          }}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {getFileDisplayIcon(node.name, fileType)}
            <div className="flex flex-col min-w-0 flex-1">
              {isLessonFile && lessonTitles.has(node.path) ? (
                <>
                  <span className="text-sm text-white/70 truncate" title={`${node.name} - ${lessonTitles.get(node.path)}`}>
                    {node.name} - {lessonTitles.get(node.path)}
                  </span>
                </>
              ) : (
                <span className="text-sm text-white/70 truncate" title={node.name}>
                  {node.name}
                </span>
              )}
            </div>
            <span className="text-xs text-white/40">
              {getFileIcon(fileType)}
            </span>
          </div>
          
          <div className="opacity-50 group-hover:opacity-100 transition-opacity">
            {renderFileAction(node.name, node.path, fileType)}
          </div>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto mb-2"></div>
        <p className="text-white/50">Loading curriculum...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <div className="text-red-400 mb-2">
          <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-red-400 text-sm mb-2">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="text-cyan-400 hover:text-cyan-300 text-sm underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="curriculum-file-browser h-full overflow-auto border border-cyan-500/20 rounded bg-black/30">
        <div className="p-3 border-b border-cyan-500/20 bg-black/40">
          <h3 className="font-medium text-white/90 flex items-center gap-2">
            <Book className="w-4 h-4" />
            Curriculum Browser
          </h3>
          <p className="text-xs text-white/50 mt-1">
            Click files to view/edit, select day*.json for execution
          </p>
        </div>
        
        <div className="p-2">
          {curriculumTree && renderTreeNode(curriculumTree)}
        </div>
      </div>

      {/* Media Viewer Modal */}
      {viewingFile && (
        <MediaViewerModal
          isOpen={mediaViewerOpen}
          onClose={() => {
            setMediaViewerOpen(false);
            setViewingFile(null);
          }}
          fileName={viewingFile.name}
          filePath={viewingFile.path}
          onSave={async (updated) => {
            const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://api.hi-si.ai';
            const isCurriculumLessonJson = /\/content\/curriculum\/baseline-en-US\/drafts\/content\/[^/]+\/day\d+\.json$/.test(viewingFile.path);
            if (isCurriculumLessonJson) {
              await fetch(`${API_BASE_URL}/api/curriculum/lesson`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ path: viewingFile.path, content: updated })
              });
            } else {
              await fetch(`${API_BASE_URL}/api/file/save`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ path: viewingFile.path, content: updated })
              });
            }
          }}
        />
      )}

      {/* Floating Audio Player */}
      {audioPlayer && (
        <FloatingAudioPlayer
          src={audioPlayer.path}
          fileName={audioPlayer.name}
          onClose={() => setAudioPlayer(null)}
        />
      )}
    </>
  );
};

export default EnhancedCurriculumBrowser;